import edgeAgentSource from '../../edge-device/aql_edge_camera_realtime.py?raw';
import edgeRequirements from '../../edge-device/requirements.txt?raw';
import { supabase } from '@/utils/supabase/client';
import { publicAnonKey, supabaseUrl } from '/utils/supabase/info';
import { createZipBlob, downloadBlob } from '@/app/utils/browser-zip';

const encoder = new TextEncoder();
const EDGE_AGENT_BUNDLE_VERSION = '2.2.1';
const safeFilePart = (value: unknown) => String(value ?? 'kit-edge')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9._-]+/g, '-')
  .replace(/^-|-$/g, '') || 'kit-edge';

const shellValue = (value: unknown) => `'${String(value ?? '').replace(/'/g, `'"'"'`)}'`;

export async function downloadKitEdgeAgent(kitId: string) {
  const [
    { data: kit, error: kitError },
    { data: cameras, error: camerasError },
    { data: agentConfig, error: agentConfigError }
  ] = await Promise.all([
    supabase.from('aql_kits').select('*').eq('kit_id', kitId).single(),
    supabase.from('aql_cameras').select('*').eq('kit_id', kitId).eq('is_active', true).order('is_primary', { ascending: false }).order('created_at', { ascending: true }),
    supabase.functions.invoke('vision-device-agent-config', { body: { kit_id: kitId } })
  ]);
  if (kitError || !kit) throw new Error(kitError?.message ?? 'Não foi possível obter a configuração da unidade Edge.');
  if (camerasError) throw new Error(camerasError.message);
  if (agentConfigError) throw new Error(agentConfigError.message || 'Não foi possível emitir a credencial do dispositivo.');

  const deviceToken = String(agentConfig?.configuration?.aql_device_token ?? '').trim();
  if (!deviceToken.startsWith('aql_dev_')) {
    throw new Error('O servidor não devolveu uma credencial de dispositivo válida.');
  }

  const camera = cameras?.[0] ?? null;
  const cameraCode = camera?.camera_code ?? `cam-${String(camera?.camera_id ?? kitId).slice(0, 8)}`;
  const frameInterval = Number(camera?.frame_interval_ms ?? kit.frame_interval_ms ?? 1000);
  const captureBucket = String(kit.live_capture_bucket ?? 'captures');
  const livePath = String(camera?.live_feed_path ?? `live/${cameraCode}/feed.jpg`);
  const kitName = String(kit.name ?? kitId);
  const bundleName = `aql-vision-agent-${safeFilePart(kitName)}-v${EDGE_AGENT_BUNDLE_VERSION.replace(/\./g, '-')}`;
  // OpenCV/Picamera2 são instalados pelos pacotes do Raspberry Pi OS. Evita
  // descarregar uma segunda cópia pesada e potencialmente incompatível via pip.
  const deviceRequirements = edgeRequirements
    .split('\n')
    .filter((line) => !/^opencv-python(?:[<=>~!]|$)/i.test(line.trim()))
    .join('\n');

  const environment = [
    `SUPABASE_URL=${shellValue(supabaseUrl)}`,
    `SUPABASE_KEY=${shellValue(publicAnonKey)}`,
    `AQL_DEVICE_TOKEN=${shellValue(deviceToken)}`,
    `AQL_KIT_ID=${shellValue(kitId)}`,
    `AQL_KIT_NAME=${shellValue(kitName)}`,
    `AQL_CAMERA_ID=${shellValue(camera?.camera_id ?? '')}`,
    `AQL_CAMERA_CODE=${shellValue(cameraCode)}`,
    `AQL_FRAME_INTERVAL_MS=${shellValue(Number.isFinite(frameInterval) ? frameInterval : 1000)}`,
    `AQL_CAPTURES_BUCKET=${shellValue(captureBucket)}`,
    `AQL_LIVE_FRAME_PATH=${shellValue(livePath)}`,
    "AQL_UPLOAD_MODE='function'",
    "AQL_CAMERA_BACKEND='auto'",
    "AQL_QUEUE_DIR='/var/lib/aql-vision/queue'",
    "AQL_MODEL_DIR='/var/lib/aql-vision/models'",
    "AQL_MODEL_SYNC_INTERVAL_SECONDS='300'",
    "AQL_MODEL_CONFIDENCE='0.35'",
    "AQL_MODEL_NMS_THRESHOLD='0.45'"
  ].join('\n');

  const installScript = `#!/usr/bin/env bash
set -euo pipefail
trap 'echo; echo "A instalação não ficou concluída."; echo "Executa: sudo ./diagnostico.sh"' ERR

SERVICE_NAME="aql-vision-raspberry-agent.service"
SERVICE_USER="aql-vision"
SOURCE_DIR="$(cd "$(dirname "$0")" && pwd)"
INSTALL_DIR="/opt/aql-vision-agent"
CONFIG_DIR="/etc/aql-vision"
DATA_DIR="/var/lib/aql-vision"

if [ "$(id -u)" -ne 0 ]; then
  echo "A instalação precisa de permissões de administrador."
  echo "Executa apenas: sudo ./install.sh"
  exit 1
fi
if [ ! -f "$SOURCE_DIR/.env" ]; then
  echo "O pacote está incompleto: falta o ficheiro de configuração."
  exit 1
fi
if [ ! -f /etc/os-release ] || ! command -v systemctl >/dev/null 2>&1; then
  echo "Este instalador requer Raspberry Pi OS ou outro Linux com systemd."
  exit 1
fi

echo "[1/6] A preparar o Raspberry Pi..."
export DEBIAN_FRONTEND=noninteractive
apt-get update -qq
apt-get install -y -qq python3-venv python3-pip python3-opencv ca-certificates curl >/dev/null
if grep -qi raspberry /proc/device-tree/model 2>/dev/null; then
  apt-get install -y -qq python3-picamera2 >/dev/null
  CAMERA_BACKEND="picamera2"
else
  CAMERA_BACKEND="opencv"
fi

echo "[2/6] A criar o serviço AQL Vision..."
if ! id "$SERVICE_USER" >/dev/null 2>&1; then
  useradd --system --home-dir "$DATA_DIR" --create-home --shell /usr/sbin/nologin "$SERVICE_USER"
fi
for camera_group in video render; do
  if getent group "$camera_group" >/dev/null 2>&1; then
    usermod -a -G "$camera_group" "$SERVICE_USER"
  fi
done

install -d -m 0755 "$INSTALL_DIR" "$CONFIG_DIR"
install -d -o "$SERVICE_USER" -g "$SERVICE_USER" -m 0750 "$DATA_DIR" "$DATA_DIR/queue" "$DATA_DIR/models"
install -m 0644 "$SOURCE_DIR/aql_edge_camera_realtime.py" "$INSTALL_DIR/aql_edge_camera_realtime.py"
install -m 0644 "$SOURCE_DIR/requirements.txt" "$INSTALL_DIR/requirements.txt"
install -m 0600 "$SOURCE_DIR/.env" "$CONFIG_DIR/agent.env"
if grep -q '^AQL_CAMERA_BACKEND=' "$CONFIG_DIR/agent.env"; then
  sed -i "s/^AQL_CAMERA_BACKEND=.*/AQL_CAMERA_BACKEND='$CAMERA_BACKEND'/" "$CONFIG_DIR/agent.env"
else
  echo "AQL_CAMERA_BACKEND='$CAMERA_BACKEND'" >> "$CONFIG_DIR/agent.env"
fi

echo "[3/6] A instalar os componentes necessários..."
python3 -m venv --system-site-packages "$INSTALL_DIR/.venv"
"$INSTALL_DIR/.venv/bin/python" -m pip install --disable-pip-version-check -q --upgrade pip
"$INSTALL_DIR/.venv/bin/python" -m pip install --disable-pip-version-check -q -r "$INSTALL_DIR/requirements.txt"

echo "[4/6] A configurar o arranque automático..."
cat > "/etc/systemd/system/$SERVICE_NAME" <<SERVICE
[Unit]
Description=AQL Vision Raspberry Agent
After=network-online.target
Wants=network-online.target

[Service]
Type=simple
User=$SERVICE_USER
Group=$SERVICE_USER
WorkingDirectory=$INSTALL_DIR
EnvironmentFile=$CONFIG_DIR/agent.env
ExecStart=$INSTALL_DIR/.venv/bin/python $INSTALL_DIR/aql_edge_camera_realtime.py
Restart=always
RestartSec=5
NoNewPrivileges=true

[Install]
WantedBy=multi-user.target
SERVICE

# Garante que corre com o utilizador técnico mesmo que tenha ficado um override
# de uma tentativa de instalação anterior.
install -d -m 0755 "/etc/systemd/system/$SERVICE_NAME.d"
cat > "/etc/systemd/system/$SERVICE_NAME.d/99-aql-installer.conf" <<SERVICE_OVERRIDE
[Service]
User=$SERVICE_USER
Group=$SERVICE_USER
SERVICE_OVERRIDE

chown -R "$SERVICE_USER:$SERVICE_USER" "$INSTALL_DIR" "$DATA_DIR"
systemctl daemon-reload
systemctl enable "$SERVICE_NAME" >/dev/null

echo "[5/6] A iniciar a câmara e a ligação..."
set -a
. "$CONFIG_DIR/agent.env"
set +a
MODEL_STATUS="$(curl -sS -o /tmp/aql-vision-agent-auth.json -w '%{http_code}' \
  -X POST "$SUPABASE_URL/functions/v1/vision-kit-model" \
  -H "apikey: $SUPABASE_KEY" \
  -H "x-aql-device-token: $AQL_DEVICE_TOKEN" \
  -H 'content-type: application/json' \
  --data "{\"kit_id\":\"$AQL_KIT_ID\"}")"
rm -f /tmp/aql-vision-agent-auth.json
case "$MODEL_STATUS" in
  200|404|409) ;;
  *)
    echo "A credencial deste pacote não foi aceite pelo servidor (HTTP $MODEL_STATUS)."
    echo "Descarrega novamente o agente e executa o install.sh dessa pasta nova."
    exit 1
    ;;
esac
systemctl restart "$SERVICE_NAME"
sleep 8

echo "[6/6] A confirmar a instalação..."
if systemctl is-active --quiet "$SERVICE_NAME"; then
  echo
  echo "✓ Instalação concluída. O AQL Vision está a funcionar."
  echo "  Câmara: $CAMERA_BACKEND"
  echo "  Unidade: ${kitName}"
  echo
  echo "Já podes voltar ao AQL Vision e confirmar a imagem."
else
  echo
  echo "O serviço não conseguiu arrancar. Últimas informações:"
  journalctl -u "$SERVICE_NAME" -n 40 --no-pager || true
  exit 1
fi
`;

  const runScript = `#!/usr/bin/env bash
set -euo pipefail
if [ "$(id -u)" -ne 0 ]; then
  exec sudo "$0" "$@"
fi
systemctl restart aql-vision-raspberry-agent.service
if systemctl is-active --quiet aql-vision-raspberry-agent.service; then
  echo "✓ O AQL Vision está a funcionar."
else
  echo "O AQL Vision não conseguiu arrancar. Executa: sudo ./diagnostico.sh"
  exit 1
fi
`;

  const diagnosticScript = `#!/usr/bin/env bash
set -u
SERVICE_NAME="aql-vision-raspberry-agent.service"
echo "Diagnóstico AQL Vision"
echo "====================="
if [ "$(id -u)" -ne 0 ]; then
  exec sudo "$0" "$@"
fi
echo
echo "Dispositivo: $(tr -d '\\0' < /proc/device-tree/model 2>/dev/null || uname -m)"
echo "Sistema: $(. /etc/os-release 2>/dev/null; echo "\${PRETTY_NAME:-Linux}")"
echo "Serviço: $(systemctl is-active "$SERVICE_NAME" 2>/dev/null || echo não instalado)"
echo "Arranque automático: $(systemctl is-enabled "$SERVICE_NAME" 2>/dev/null || echo não)"
echo "Utilizador: $(systemctl show "$SERVICE_NAME" -p User --value 2>/dev/null || echo desconhecido)"
echo "Configuração: $([ -r /etc/aql-vision/agent.env ] && echo presente || echo em falta)"
echo "Câmara: $(grep -E '^AQL_CAMERA_BACKEND=' /etc/aql-vision/agent.env 2>/dev/null | cut -d= -f2 || echo desconhecida)"
if [ -r /var/lib/aql-vision/models/current.json ]; then
  echo "Modelo Edge: instalado"
else
  echo "Modelo Edge: a aguardar sincronização"
fi
echo
echo "Últimas mensagens:"
journalctl -u "$SERVICE_NAME" -n 60 --no-pager 2>/dev/null || true
`;

  const readme = `# Agente AQL Vision — ${kitName}

Pacote preparado para a unidade Edge **${kitName}** e para a câmara **${camera?.name ?? cameraCode}**.

1. Extrai o ZIP no Raspberry Pi.
2. Abre um terminal dentro da pasta extraída.
3. Executa apenas \`sudo bash install.sh\`.

O instalador deteta a câmara, instala os componentes necessários, configura as permissões, cria o serviço e confirma que ficou ativo. Não precisas de editar ficheiros nem de conhecer Linux.

A credencial não expira com o tempo. Só é invalidada se um administrador a revogar ou se for descarregado um novo agente para esta unidade.

O agente publica o frame em \`${captureBucket}/${livePath}\`, descarrega automaticamente o ONNX aprovado, executa a inferência no Raspberry Pi e envia os resultados para as regras do AQL Vision. Quando aprovares uma versão nova, a unidade atualiza-a automaticamente em até cinco minutos.

Se surgir algum problema, executa \`sudo bash diagnostico.sh\` e envia o resultado ao suporte AQL.
`;

  const blob = createZipBlob([
    { name: `${bundleName}/aql_edge_camera_realtime.py`, data: encoder.encode(edgeAgentSource) },
    { name: `${bundleName}/requirements.txt`, data: encoder.encode(deviceRequirements) },
    { name: `${bundleName}/.env`, data: encoder.encode(environment) },
    { name: `${bundleName}/install.sh`, data: encoder.encode(installScript) },
    { name: `${bundleName}/run.sh`, data: encoder.encode(runScript) },
    { name: `${bundleName}/diagnostico.sh`, data: encoder.encode(diagnosticScript) },
    { name: `${bundleName}/README.md`, data: encoder.encode(readme) }
  ]);
  downloadBlob(blob, `${bundleName}.zip`);
}
